/* eslint-disable max-classes-per-file */
import { applyPatch, compare, deepClone, generate, observe, Observer, Operation } from 'fast-json-patch';
import { Entry } from '../db/DB';
import { Replicant, ReplicantConfig } from '../ReplicantConfig';
import ReplicantMigrator from '../ReplicantMigrator';
import SB from '../SchemaBuilder';
import { withJsonPatchDiff } from '../server/utils/JSONPatchUtils';

export type UnknownState = Record<string, unknown>;

export type UnknownEntry = Entry<UnknownState>;

export type VirtualStateDiff = {
    state?: Operation[];
    metainfo?: Operation[];
    lastLoginAt?: number;
    migratedToVersion?: number;
};

export function createVirtualEntry<T extends Replicant>(
    config: ReplicantConfig<T>,
    entry: UnknownEntry | VirtualEntry<T['state']>,
): VirtualEntry<T['state']> {
    return entry instanceof VirtualEntry ? entry : new VirtualEntry(entry, config.migrator, config.stateSchema);
}

export class VirtualEntry<T extends UnknownState> {
    /** A copy of the entry after having migrations applied to the persisted state . */
    protected migrated: UnknownEntry;

    /** A copy of the entry after running the migrated state through a decoder. */
    protected decoded: Entry<T>;

    /** The operations performed to fill in missing "default" state _after_ migration. */
    protected backfillOperations: Operation[];

    /** Change observer for the state mutated by the game. */
    protected stateObserver: Observer<T>;

    private originalLastLoginAt?: number;
    private originalVersion: number;

    constructor(entry: UnknownEntry, migrator: ReplicantMigrator, schema: SB.Schema<T>) {
        this.originalLastLoginAt = entry.lastLoginAt;
        this.originalVersion = entry.version;

        const { migratedEntry, migrationOperations } = this.migrate(entry, migrator);

        this.migrated = migratedEntry;
        this.decoded = {
            ...this.migrated,
            state: schema.tryDecode(this.migrated.state),
            metainfo: deepClone(this.migrated.metainfo),
        };

        // track which fields have been added/updated during the decoding
        const decodingOperations = compare(this.migrated.state, this.decoded.state).filter(({ op }) => op !== 'remove');

        if (decodingOperations.length) {
            // and apply the changes to the migrated state
            applyPatch(this.migrated.state, decodingOperations, false, true);
        }

        this.backfillOperations = [...migrationOperations, ...decodingOperations];

        // set up the state mutation observer. a few notes:
        //   - this can't be done with metainfo because other parts of Replicant can replace it with a new reference
        //   - if an entirely new state reference is swapped in this will break. it requires state mutation :(
        this.stateObserver = observe<T>(this.decoded.state);
    }

    /** The working entry for this virtual state. */
    get entry(): Entry<T> {
        return this.decoded;
    }

    /** Build a new state snapshot. */
    snapshot(): Snapshot {
        // calculate the diff
        const stateOperations = generate(this.stateObserver);
        const metainfoOperations = compare(this.migrated.metainfo, this.decoded.metainfo);
        // build a new entry to return
        const entry = {
            ...this.decoded,
            // the backfill operations are already applied to the migrated state, so skip here
            state: applyPatch(this.migrated.state, stateOperations, false, false).newDocument,
            metainfo: applyPatch(this.migrated.metainfo, metainfoOperations, false, false).newDocument,
        };
        return new Snapshot(entry, {
            // pass along the backfill operations for use in state recovery
            state: this.backfillOperations.concat(stateOperations),
            metainfo: metainfoOperations,
            lastLoginAt: this.decoded.lastLoginAt !== this.originalLastLoginAt ? this.decoded.lastLoginAt : undefined,
            migratedToVersion: this.decoded.version > this.originalVersion ? this.decoded.version : undefined,
        });
    }

    /** Clones the entry, migrates the state of the clone and returns the new clone. */
    protected migrate(
        entry: UnknownEntry,
        migrator: ReplicantMigrator,
    ): { migratedEntry: UnknownEntry; migrationOperations: Operation[] } {
        const migratedEntry = deepClone(entry);
        let migrationOperations: Operation[] = [];

        const hasMigrationsToApply = (): boolean => migratedEntry.version < migrator.getLatestVersion();

        if (hasMigrationsToApply()) {
            migrationOperations = withJsonPatchDiff(migratedEntry.state, () => {
                while (hasMigrationsToApply()) {
                    migrator.applyMigration(migratedEntry.state, migratedEntry.version);
                    migratedEntry.version += 1;
                }
            });
        }

        return { migratedEntry, migrationOperations };
    }
}

/** Represents a persistable entry, along with the operations made to the original entry to achieve it. */
export class Snapshot {
    constructor(
        public entry: UnknownEntry,
        private opts: { state: Operation[]; metainfo: Operation[]; migratedToVersion?: number; lastLoginAt?: number },
    ) {}

    get diff(): VirtualStateDiff {
        const diff: VirtualStateDiff = {
            lastLoginAt: this.opts.lastLoginAt,
            migratedToVersion: this.opts.migratedToVersion,
        };

        if (this.opts.state.length) {
            diff.state = this.opts.state;
        }
        if (this.opts.metainfo.length) {
            diff.metainfo = this.opts.metainfo;
        }
        return diff;
    }
}
